import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Group, User } from '@microsoft/microsoft-graph-types-beta';
import { map, switchMap, take } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import {
    AccessType,
    AppliesTo,
    CrossTenantAccessSettingsSchema,
    extractCrossTenantAccessSettings,
    generateCrossTenantAccessSettingsSchema,
    OutboundSettingKey,
    policyAppliesTo,
    readPolicyAccessType,
    readPolicyTargets,
    SettingKey,
} from '../../../../../tools/schemas/graph/cross-tenant-access-settings.schema';
import { ManageItemsWrapperDialogComponent } from '../conditional-access-policy/shared/manage-items-wrapper-dialog/manage-items-wrapper-dialog.component';
import { CapItem, RowType } from '../conditional-access-policy/shared/model';
import { CrossTenantAccessPolicyDefault } from 'projects/angular-clarity/src/app/stores/client/graph/cross-tenant-access-policy/actions';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { selectSession } from 'projects/angular-clarity/src/app/stores/root.store';
import { loadGraphUsers } from 'projects/angular-clarity/src/app/stores/client/graph/user/user.actions';
import { loadGroups } from 'projects/angular-clarity/src/app/stores/client/graph/group/actions';

/** A row that exposes both a users-and-groups and an applications access type. */
interface AccessSetting {
    label: string;
    /** Form control name for users-and-groups — also the key on the live policy object. */
    usersKey: SettingKey;
    /** Form control name for the applications access type. */
    appsKey: string;
    /**
     * Outbound settings additionally let you narrow users-and-groups to specific targets. When set,
     * these name the "applies to" mode control and the selected user/group id list controls.
     */
    appliesToKey?: string;
    usersListKey?: string;
    groupsListKey?: string;
}

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit {
    data: CrossTenantAccessPolicyDefault;

    accessTypeOptions: { label: string; value: AccessType }[] = [
        { label: 'Allowed', value: 'allowed' },
        { label: 'Blocked', value: 'blocked' },
    ];

    readonly outboundSettings: AccessSetting[] = [
        {
            label: 'B2B collaboration',
            usersKey: 'b2bCollaborationOutbound',
            appsKey: 'b2bCollaborationOutboundApps',
            appliesToKey: 'b2bCollaborationOutboundAppliesTo',
            usersListKey: 'b2bCollaborationOutboundUsers',
            groupsListKey: 'b2bCollaborationOutboundGroups',
        },
        {
            label: 'B2B direct connect',
            usersKey: 'b2bDirectConnectOutbound',
            appsKey: 'b2bDirectConnectOutboundApps',
            appliesToKey: 'b2bDirectConnectOutboundAppliesTo',
            usersListKey: 'b2bDirectConnectOutboundUsers',
            groupsListKey: 'b2bDirectConnectOutboundGroups',
        },
    ];

    appliesToOptions: { label: string; value: AppliesTo }[] = [
        { label: 'All users', value: 'all' },
        { label: 'Select users and groups', value: 'select' },
    ];

    /** Tenant users/groups used to populate and resolve the "select users and groups" picker. */
    usersLookup: User[] = [];
    groupsLookup: Group[] = [];

    readonly tenantRestrictionSettings: AccessSetting[] = [
        { label: 'Tenant restrictions', usersKey: 'tenantRestrictions', appsKey: 'tenantRestrictionsApps' },
    ];

    readonly inboundSettings: AccessSetting[] = [
        { label: 'B2B collaboration', usersKey: 'b2bCollaborationInbound', appsKey: 'b2bCollaborationInboundApps' },
        { label: 'B2B direct connect', usersKey: 'b2bDirectConnectInbound', appsKey: 'b2bDirectConnectInboundApps' },
    ];

    constructor(
        private readonly fb: FormBuilder,
        private readonly dialog: MatDialog,
        private readonly store: Store<any>,
    ) {
        super();
    }

    /**
     * Root-form validator: every outbound "Applies to" must target someone — either "All users",
     * or, in select mode, at least one selected user or group.
     */
    private appliesToRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control as FormGroup).getRawValue();
            const missing: Record<string, boolean> = {};

            for (const setting of this.outboundSettings) {
                if (value[setting.appliesToKey] !== 'select') continue;
                const users: string[] = value[setting.usersListKey] ?? [];
                const groups: string[] = value[setting.groupsListKey] ?? [];
                if (users.length === 0 && groups.length === 0) {
                    missing[setting.usersKey] = true;
                }
            }

            return Object.keys(missing).length > 0 ? { appliesToSelectionRequired: missing } : null;
        };
    }

    ngOnInit(): void {
        const schema = this.baseline?.schema as CrossTenantAccessSettingsSchema;
        const value = extractCrossTenantAccessSettings(schema);

        this.loadLookups();

        this.form = this.fb.group({
            isMfaAccepted: [value.isMfaAccepted],
            isCompliantDeviceAccepted: [value.isCompliantDeviceAccepted],
            isHybridAzureADJoinedDeviceAccepted: [value.isHybridAzureADJoinedDeviceAccepted],
            b2bCollaborationInbound: [value.b2bCollaborationInbound],
            b2bCollaborationInboundApps: [value.b2bCollaborationInboundApps],
            b2bCollaborationOutbound: [value.b2bCollaborationOutbound],
            b2bCollaborationOutboundApps: [value.b2bCollaborationOutboundApps],
            b2bDirectConnectInbound: [value.b2bDirectConnectInbound],
            b2bDirectConnectInboundApps: [value.b2bDirectConnectInboundApps],
            b2bDirectConnectOutbound: [value.b2bDirectConnectOutbound],
            b2bDirectConnectOutboundApps: [value.b2bDirectConnectOutboundApps],
            tenantRestrictions: [value.tenantRestrictions],
            tenantRestrictionsApps: [value.tenantRestrictionsApps],
            // Outbound users-and-groups target selection.
            b2bCollaborationOutboundAppliesTo: [value.b2bCollaborationOutboundAppliesTo],
            b2bCollaborationOutboundUsers: [value.b2bCollaborationOutboundUsers],
            b2bCollaborationOutboundGroups: [value.b2bCollaborationOutboundGroups],
            b2bDirectConnectOutboundAppliesTo: [value.b2bDirectConnectOutboundAppliesTo],
            b2bDirectConnectOutboundUsers: [value.b2bDirectConnectOutboundUsers],
            b2bDirectConnectOutboundGroups: [value.b2bDirectConnectOutboundGroups],
        });

        this.form.setValidators(this.appliesToRequiredValidator());
        this.form.updateValueAndValidity({ emitEvent: false });

        // Applications can never be more permissive than users-and-groups: whenever a direction's
        // users-and-groups access type is set to Blocked, force its applications access type to
        // Blocked too (and the template disables the "Allowed" option). This prevents the
        // "Conflicting configuration" error Microsoft Graph returns for mismatched partitions.
        this.allSettings.forEach((setting) => this.linkApplicationsToUsers(setting));

        this.form.valueChanges.subscribe(() => {
            const nextSchema = generateCrossTenantAccessSettingsSchema(this.form.getRawValue());

            this.baselineErrorChange.next({ remediate: this.form.invalid, save: this.form.invalid });

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(nextSchema)),
            });
        });
    }

    get allSettings(): AccessSetting[] {
        return [...this.outboundSettings, ...this.tenantRestrictionSettings, ...this.inboundSettings];
    }

    private linkApplicationsToUsers(setting: AccessSetting): void {
        const usersControl = this.form.get(setting.usersKey);
        const appsControl = this.form.get(setting.appsKey);

        const enforce = (emitEvent: boolean) => {
            const usersValue: AccessType = usersControl?.value;

            // Rule A: If users and groups is 'blocked', applications MUST be 'blocked' too.
            if (usersValue === 'blocked' && appsControl?.value !== 'blocked') {
                appsControl?.setValue('blocked', { emitEvent });
                return;
            }

            // Rule B: If users and groups is 'allowed', applications CANNOT be 'blocked' —
            // this applies to ALL settings including tenant restrictions.
            if (usersValue === 'allowed' && appsControl?.value === 'blocked') {
                appsControl?.setValue('allowed', { emitEvent });
            }
        };

        enforce(false);
        usersControl?.valueChanges.subscribe(() => enforce(true));
        if (setting.appliesToKey) {
            this.form.get(setting.appliesToKey)?.valueChanges.subscribe(() => enforce(true));
        }
    }

    /** Whether the applications access type is locked because users-and-groups is Blocked. */
    isApplicationsLocked(setting: AccessSetting): boolean {
        return this.form.get(setting.usersKey)?.value === 'blocked';
    }

    /** Whether the applications access type option 'blocked' is disabled because users and groups is allowed. */
    isBlockedAppsDisabled(setting: AccessSetting): boolean {
        return this.form.get(setting.usersKey)?.value === 'allowed';
    }

    // --- Outbound "applies to" target selection -------------------------------
    /** Load tenant users and groups for the "select users and groups" picker. */
    private loadLookups(): void {
        const tenant$ = this.store.select(selectSession).pipe(map((sess) => sess.session.clientId));

        const sub1 = tenant$
            .pipe(switchMap((tid) => this.store.select(client(tid).graph.users.all)))
            .subscribe((users) => (this.usersLookup = users ?? []));
        const sub2 = tenant$
            .pipe(switchMap((tid) => this.store.select(client(tid).graph.groups.all)))
            .subscribe((groups) => (this.groupsLookup = groups ?? []));

        this.subs.push(sub1, sub2);

        // Ensure the lookups are populated even if no other view has fetched them yet.
        tenant$.pipe(take(1)).subscribe((tid) => {
            if (!tid) return;
            this.store.dispatch(loadGraphUsers({ _tenant: tid }));
            this.store.dispatch(loadGroups({ _tenant: tid }));
        });
    }

    /** Whether the setting supports narrowing users-and-groups to specific targets (outbound only). */
    supportsTargets(setting: AccessSetting): boolean {
        return !!setting.appliesToKey;
    }

    /** Whether the "select users and groups" picker is currently active for the setting. */
    isSelectMode(setting: AccessSetting): boolean {
        return this.form.get(setting.appliesToKey)?.value === 'select';
    }

    selectedUsersCount(setting: AccessSetting): number {
        return this.form.get(setting.usersListKey)?.value?.length ?? 0;
    }
    selectedGroupsCount(setting: AccessSetting): number {
        return this.form.get(setting.groupsListKey)?.value?.length ?? 0;
    }

    /**
     * Whether the users/groups picker is locked for the setting. Follows the list control's
     * disabled state rather than the blanket readOnly flag, so template-level overrides
     * (which re-enable just the overridden controls) make the picker editable.
     */
    isPickerLocked(setting: AccessSetting, type: 'users' | 'groups'): boolean {
        const listKey = type === 'users' ? setting.usersListKey : setting.groupsListKey;
        return !!this.form.get(listKey)?.disabled;
    }

    /** Whether the setting's select-mode target list is empty — drives the banner and inline error. */
    appliesToSelectionMissing(setting: AccessSetting): boolean {
        return this.form.errors?.appliesToSelectionRequired?.[setting.usersKey] === true;
    }

    /** Labels of the outbound settings whose "Applies to" selection is still empty. */
    get missingTargetsLabels(): string {
        return this.outboundSettings
            .filter((setting) => this.appliesToSelectionMissing(setting))
            .map((setting) => setting.label)
            .join(', ');
    }

    /** Open the manage dialog for the setting's selected users or groups, mirroring conditional access. */
    manageItems(setting: AccessSetting, type: 'users' | 'groups'): void {
        const lookup = type === 'users' ? this.usersLookup : this.groupsLookup;
        const listKey = type === 'users' ? setting.usersListKey : setting.groupsListKey;
        const targetType = type === 'users' ? 'user' : 'group';

        const selectedIds: string[] = this.form.get(listKey)?.value ?? [];
        const policyTargets = readPolicyTargets(this.data, setting.usersKey as OutboundSettingKey) ?? [];
        const policyIds = policyTargets.filter((t) => t.targetType === targetType).map((t) => t.target);

        const allItems: CapItem[] = lookup.map((res) => ({
            displayName: res.displayName,
            id: res.id,
            inBaselineSchema: selectedIds.some((id) => id === res.id),
            inPolicy: policyIds.some((id) => id === res.id),
        }));

        const config =
            type === 'users'
                ? {
                      title: 'Users',
                      allListTitle: 'Available users',
                      desiredListTitle: 'Selected users in baseline',
                      allListComparativeTitle: 'Current tenant users',
                      type: RowType.includeUsers,
                  }
                : {
                      title: 'Groups',
                      allListTitle: 'Available groups',
                      desiredListTitle: 'Selected groups in baseline',
                      allListComparativeTitle: 'Current tenant groups',
                      type: RowType.includeGroups,
                  };

        const dialogRef = this.dialog.open(ManageItemsWrapperDialogComponent, {
            data: { allItems, ...config },
            maxHeight: '90vh',
            width: '70%',
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((res: CapItem[]) => {
                if (res instanceof Array) {
                    const ids = res.filter((item) => item.inBaselineSchema).map((item) => item.id);
                    this.form.get(listKey)?.setValue(ids);
                }
            });
    }

    /** Human-readable summary of the outbound setting's current "applies to" on the live policy. */
    currentAppliesToValue(setting: AccessSetting): string {
        if (!this.data) return 'Not configured';
        const key = setting.usersKey as OutboundSettingKey;
        if (policyAppliesTo(this.data, key) === 'all') return 'All users';

        const targets = readPolicyTargets(this.data, key) ?? [];
        const users = targets.filter((t) => t.targetType === 'user').length;
        const groups = targets.filter((t) => t.targetType === 'group').length;
        return `Selected (${users} users, ${groups} groups)`;
    }

    /** Whether the outbound "applies to" selection deviates from the live policy. */
    appliesToDeviated(setting: AccessSetting): boolean {
        if (!this.data) return false;
        const key = setting.usersKey as OutboundSettingKey;

        const desiredMode: AppliesTo = this.form.get(setting.appliesToKey)?.value;
        if (policyAppliesTo(this.data, key) !== desiredMode) return true;
        if (desiredMode === 'all') return false;

        const targets = readPolicyTargets(this.data, key) ?? [];
        const policyKeys = new Set(targets.map((t) => `${t.targetType}:${t.target}`));
        const desiredKeys = new Set([
            ...((this.form.get(setting.usersListKey)?.value ?? []) as string[]).map((id) => `user:${id}`),
            ...((this.form.get(setting.groupsListKey)?.value ?? []) as string[]).map((id) => `group:${id}`),
        ]);

        if (policyKeys.size !== desiredKeys.size) return true;
        return [...desiredKeys].some((k) => !policyKeys.has(k));
    }

    // --- Current value + deviation helpers ------------------------------------
    currentUsersValue(setting: AccessSetting): string {
        return readPolicyAccessType(this.data, setting.usersKey, 'usersAndGroups') ?? 'Not configured';
    }
    currentApplicationsValue(setting: AccessSetting): string {
        return readPolicyAccessType(this.data, setting.usersKey, 'applications') ?? 'Not configured';
    }
    usersDeviated(setting: AccessSetting): boolean {
        return (
            !!this.data &&
            readPolicyAccessType(this.data, setting.usersKey, 'usersAndGroups') !==
                this.form.get(setting.usersKey)?.value
        );
    }
    applicationsDeviated(setting: AccessSetting): boolean {
        return (
            !!this.data &&
            readPolicyAccessType(this.data, setting.usersKey, 'applications') !== this.form.get(setting.appsKey)?.value
        );
    }

    // --- Inbound trust helpers -------------------------------------------------
    private trustLabel(value: boolean | undefined): string {
        if (value === true) return 'Trusted';
        if (value === false) return 'Not trusted';
        return 'Not configured';
    }

    get isMfaAcceptedCurrentValue(): string {
        return this.trustLabel(this.data?.inboundTrust?.isMfaAccepted);
    }
    get isCompliantDeviceAcceptedCurrentValue(): string {
        return this.trustLabel(this.data?.inboundTrust?.isCompliantDeviceAccepted);
    }
    get isHybridAzureADJoinedDeviceAcceptedCurrentValue(): string {
        return this.trustLabel(this.data?.inboundTrust?.isHybridAzureADJoinedDeviceAccepted);
    }
    get isMfaAcceptedDeviated(): boolean {
        return !!this.data && this.data?.inboundTrust?.isMfaAccepted !== this.form.get('isMfaAccepted')?.value;
    }
    get isCompliantDeviceAcceptedDeviated(): boolean {
        return (
            !!this.data &&
            this.data?.inboundTrust?.isCompliantDeviceAccepted !== this.form.get('isCompliantDeviceAccepted')?.value
        );
    }
    get isHybridAzureADJoinedDeviceAcceptedDeviated(): boolean {
        return (
            !!this.data &&
            this.data?.inboundTrust?.isHybridAzureADJoinedDeviceAccepted !==
                this.form.get('isHybridAzureADJoinedDeviceAccepted')?.value
        );
    }
}
